import React, { useEffect } from 'react';
import { X, CreditCard, Users, LifeBuoy, Star, Mail, UserPlus, Bell, AlertTriangle } from 'lucide-react';
import { Notification, NotificationCategory, CATEGORY_COLORS } from '../../types/notifications';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

// Mapping des icônes par catégorie
const getCategoryIcon = (category?: NotificationCategory) => {
  switch (category) {
    case 'stripe':
      return CreditCard;
    case 'crm':
      return Users;
    case 'support':
      return LifeBuoy;
    case 'feature':
      return Star;
    case 'newsletter':
      return Mail;
    case 'onboarding':
      return UserPlus;
    default:
      return Bell;
  }
};

interface NotificationToastProps {
  notification: Notification;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onMarkAsRead,
}) => {
  const Icon = getCategoryIcon(notification.category);
  const categoryColor = notification.category
    ? CATEGORY_COLORS[notification.category]
    : 'bg-neutral-100 text-neutral-600';

  // Marquer comme lu et fermer automatiquement après 5 secondes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!notification.read) {
        onMarkAsRead(notification.id);
      }
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.id, notification.read, onClose, onMarkAsRead]);

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    onClose();
  };

  return (
    <div
      className={`
        w-96 max-w-[calc(100vw-2rem)]
        bg-white rounded-lg shadow-lg border border-neutral-200
        transform transition-all duration-300 ease-in-out
        ${!notification.read ? 'ring-2 ring-[#0066FF]/20 ring-opacity-50' : ''}
      `}
      onClick={handleClick}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${categoryColor}`}>
            <Icon className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-semibold truncate ${!notification.read ? 'text-neutral-900' : 'text-neutral-700'}`}>
                    {notification.title}
                  </p>
                  {!notification.read && (
                    <span className="w-2 h-2 bg-[#0066FF] rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-neutral-600 line-clamp-2 mt-1">
                  {notification.content}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-neutral-400">
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: fr })}
                  </span>
                  {notification.priority === 'urgent' && (
                    <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                      <AlertTriangle className="w-3 h-3" />
                      Urgent
                    </span>
                  )}
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!notification.read) {
                    onMarkAsRead(notification.id);
                  }
                  onClose();
                }}
                className="flex-shrink-0 p-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar pour l'auto-fermeture */}
      <div className="h-1 bg-neutral-100 rounded-b-lg overflow-hidden">
        <div
          className="h-full bg-[#0066FF]"
          style={{
            animation: 'shrink 5s linear forwards',
          }}
        />
      </div>
      <style>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

// Container pour gérer plusieurs toasts
interface NotificationToastContainerProps {
  toasts: Notification[];
  onClose: (id: string) => void;
  onMarkAsRead: (id: string) => void;
}

export const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  toasts,
  onClose,
  onMarkAsRead,
}) => {
  if (toasts.length === 0) return null;

  return (
    <div 
      className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none"
      style={{
        maxHeight: 'calc(100vh - 2rem)',
        maxWidth: 'calc(100vw - 2rem)',
      }}
    >
      {toasts.map((notification, index) => (
        <div
          key={notification.id}
          className="pointer-events-auto"
          style={{
            animation: 'slideInRight 0.3s ease-out',
            animationDelay: `${index * 50}ms`,
            animationFillMode: 'both',
          }}
        >
          <NotificationToast
            notification={notification}
            onClose={() => onClose(notification.id)}
            onMarkAsRead={onMarkAsRead}
          />
        </div>
      ))}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
